
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Copy, Share2, Sparkles, Users, ChevronRight } from 'lucide-react';
import { toast } from '@/lib/utils/toast';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';
import type { FusedRecommendation } from '@/services/recommendations-fusion.service';
import { 
  formatCollectionForWhatsApp, 
  generateWhatsAppUrl, 
  copyToClipboard, 
  createShareableLink 
} from '@/utils/whatsapp-formatter';
import { collectionAnalytics } from '@/services/collection-analytics.service';

interface CollectionShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: (FusedRecommendation | any)[];
  destination: string;
  title?: string;
}

export function CollectionShareModal({
  isOpen,
  onClose,
  items,
  destination,
  title: initialTitle
}: CollectionShareModalProps) {
  const { user, profile } = useUnifiedAuth();
  const [title, setTitle] = useState(initialTitle || `My ${destination} Shortlist`);
  const [isCreating, setIsCreating] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [waMessage, setWaMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  async function createShortlist() {
    if (!user) {
      toast.error('Please sign in to share a shortlist');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to share a shortlist');
        setIsCreating(false);
        return;
      }
      
      const response = await fetch('/.netlify/functions/shortlist-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          city: destination,
          title,
          venueIds: items.map(item => item.id)
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create shortlist');
      }
      
      const data = await response.json();
      setShareToken(data.shareToken);
      setCollectionId(typeof data.id === 'string' ? data.id : null);
      
      const url = createShareableLink(data.shareToken, 'whatsapp');
      setShareUrl(url);
      
      const curatorName = profile?.display_name || profile?.full_name || 'Friend';
      const message = formatCollectionForWhatsApp(
        items,
        destination,
        {
          curatorName,
          curatorContext: `${items.length} hand-picked spots`,
          collectionUrl: url,
          maxVenues: 5,
          includeBookingLinks: true,
          includeDescription: false
        }
      );
      setWaMessage(message);
      
      collectionAnalytics.trackCollectionCreated(
        data.id,
        destination,
        items.length,
        user?.id
      );
      
    } catch (error) {
      console.error('Failed to create shortlist:', error);
      toast.error('Failed to create shortlist');
    } finally {
      setIsCreating(false);
    }
  }
  
  async function handleShare(method: 'whatsapp' | 'copy' | 'native') {
    if (!shareToken) {
      await createShortlist();
      if (!shareToken) return;
    }
    
    // On mobile, prefer native share even for WhatsApp button
    const isMobile = /mobile|android|iphone/i.test(navigator.userAgent);
    if (isMobile && navigator.share && shareUrl && method !== 'copy') {
      try {
        await navigator.share({
          title: title,
          text: `Check out my ${destination} recommendations`,
          url: shareUrl
        });
        collectionAnalytics.trackCollectionShared(collectionId ?? '', shareToken, 'native', user?.id);
        return;
      } catch (err) {
        // User cancelled or error - fall through to specific method
        if ((err as Error).name === 'AbortError') return;
      }
    }
    
    if (method === 'whatsapp' && waMessage) {
      const waUrl = generateWhatsAppUrl(waMessage);
      window.open(waUrl, '_blank');
      
      if (shareToken) {
        collectionAnalytics.trackCollectionShared(
          collectionId ?? '',
          shareToken,
          'whatsapp',
          user?.id
        );
      }
      
    } else if (method === 'copy' && waMessage) {
      const success = await copyToClipboard(waMessage);
      if (success) {
        setCopied(true);
        toast.success('Collection copied to clipboard • Expires in 7 days');
        setTimeout(() => setCopied(false), 3000);
      }
      
    } else if (method === 'native' && navigator.share && shareUrl) {
      try {
        await navigator.share({
          title: title,
          text: `Check out my ${destination} recommendations`,
          url: shareUrl
        });
      } catch {
        console.log('Share cancelled');
      }
    }
  }
  
  const previewItems = items.slice(0, 3);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-x-4 top-[10vh] max-w-lg mx-auto bg-bone rounded-2xl shadow-2xl z-50 max-h-[80vh] overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-sand-new flex items-center justify-between">
              <h2 className="font-brand-display text-xl text-charcoal flex items-center gap-2">
                <Share2 className="w-5 h-5 text-olive" />
                Share Shortlist
              </h2>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-parchment transition-colors"
              >
                <X className="w-5 h-5 text-ash" />
              </button>
            </div>
            
            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
              {/* Title Input */}
              <div className="mb-4">
                <label className="text-xs text-ash mb-1 block">Shortlist Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`My ${destination} Picks`}
                  className="w-full px-3 py-2 bg-parchment border border-sand-new rounded-lg text-charcoal placeholder-ash/50 focus:outline-none focus:ring-2 focus:ring-olive/30"
                />
              </div>
              
              {/* Preview */}
              <div className="mb-4">
                <label className="text-xs text-ash mb-2 block">Preview (first 3 venues)</label>
                <div className="bg-parchment rounded-lg p-3 space-y-2">
                  {previewItems.map((item, index) => (
                    <div key={item.id} className="flex items-start gap-2">
                      <span className="text-sm text-olive font-medium">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="text-sm text-charcoal font-medium">{item.place_name}</div>
                        <div className="text-xs text-ash">
                          {item.category} • {item.detected_neighborhood || item.neighborhood}
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <div className="text-xs text-ash italic pt-1">
                      +{items.length - 3} more venues...
                    </div>
                  )}
                </div>
              </div>
              
              {/* Social Proof Preview */}
              <div className="mb-4 p-3 bg-olive/5 rounded-lg border border-olive/20">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-olive mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm text-charcoal font-medium">How it appears to friends</div>
                    <div className="text-xs text-ash mt-1">
                      Non-members see 3 venues, members see 7, you see all {items.length}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Share Message Preview */}
              {waMessage && (
                <div className="mb-4">
                  <label className="text-xs text-ash mb-2 block">WhatsApp Message</label>
                  <div className="bg-parchment rounded-lg p-3 text-xs text-charcoal whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                    {waMessage.split('\\n').join('\n')}
                  </div>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="px-6 py-4 border-t border-sand-new bg-parchment/50">
              {!shareToken ? (
                <button
                  onClick={createShortlist}
                  disabled={isCreating || items.length === 0}
                  className="w-full px-4 py-3 bg-olive text-bone rounded-lg font-medium hover:bg-olive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-bone border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Create Shortlist
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="w-full px-4 py-3 bg-olive text-bone rounded-lg font-medium hover:bg-olive/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Share to WhatsApp
                  </button>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleShare('copy')}
                      className="px-4 py-2 bg-bone border border-sand-new rounded-lg text-sm text-charcoal hover:bg-parchment transition-colors flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    
                    {'share' in navigator && typeof navigator.share === 'function' && (
                      <button
                        onClick={() => handleShare('native')}
                        className="px-4 py-2 bg-bone border border-sand-new rounded-lg text-sm text-charcoal hover:bg-parchment transition-colors flex items-center justify-center gap-2"
                      >
                        <Share2 className="w-4 h-4" />
                        Share
                      </button>
                    )}
                  </div>
                  
                  {shareUrl && (
                    <div className="pt-2 text-center">
                      <a 
                        href={shareUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-olive hover:underline inline-flex items-center gap-1"
                      >
                        View shortlist (expires in 7 days)
                        <ChevronRight className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
